import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInterval } from '~/hooks/use-interval'
import { useFormikContext } from 'formik'
import { apiError } from '~/utils/api-error'
import { useSignInWithGmailMutation } from '~/pages/sign-in/graphql/sign-in-with-gmail'
import { useInvitation } from '~/hooks/use-invitation'
import { useSignInUser } from '~/hooks/use-sign-in-user'
import { useToasts } from '~/context/toasts'
import { useSignUpWithGmailMutation } from '~/pages/sign-up/graphql/sign-up-with-gmail'

const GoogleAuthButton = ({ type }: { type: 'sign-in' | 'sign-up' }) => {
  const toasts = useToasts()

  const isSignUp = useMemo(() => type === 'sign-up', [type])

  const [signInWithGmail] = useSignInWithGmailMutation()
  const [signUpWithGmail] = useSignUpWithGmailMutation()

  const invitation = useInvitation()
  const signInUser = useSignInUser()

  const [googleInstance, setGoogleInstance] = useState<any>(null)
  const [googleIdentityScriptLoaded, setGoogleIdentityScriptLoaded] =
    useState<boolean>(false)

  const buttonRef = useRef<HTMLDivElement>(null)
  const { setFieldError } = useFormikContext()

  // @ts-ignore
  const googleAuthButtonOptions: google.accounts.id.GsiButtonConfiguration =
    useMemo(
      () => ({
        type: 'standard',
        theme: 'filled_blue',
        size: 'large',
        text: isSignUp ? 'signup_with' : 'signin_with',
        shape: 'rectangular',
        width: 400,
      }),
      [isSignUp]
    )

  const handleSignUp = async (token: string) => {
    try {
      const { data } = await signUpWithGmail({
        variables: {
          input: {
            idToken: token,
            invitation: invitation?.input,
          },
        },
      })

      if (data) {
        await signInUser(data.signUpWithGmail.accessToken)
      }
    } catch (error) {
      apiError(error, {
        gmail_not_found: () =>
          toasts.warning('User with specified email does not exist'),
        invalid_invitation_token: () =>
          toasts.warning('Invitation is not valid for this email'),
      })
    }
  }

  const handleSignIn = async (token: string) => {
    try {
      const { data } = await signInWithGmail({
        variables: {
          input: {
            idToken: token,
            invitation: invitation?.input,
          },
        },
      })

      if (data) {
        await signInUser(data.signInWithGmail.accessToken)
      }
    } catch (error) {
      apiError(error, {
        gmail_not_found: () =>
          toasts.warning('User with specified email does not exist'),
        invalid_invitation_token: () =>
          toasts.warning('Invitation is not valid for this email'),
      })
    }
  }

  // Periodically check if the google object is loaded.
  useInterval(
    () => {
      // @ts-ignore
      if (typeof window !== 'undefined' && window.google) {
        // @ts-ignore
        setGoogleInstance(window.google)
        setGoogleIdentityScriptLoaded(true)
      }
    },
    googleIdentityScriptLoaded ? null : 100
  )

  const resizeAuthButton = useCallback(
    (width: number) => {
      if (googleInstance) {
        googleInstance.accounts.id.renderButton(buttonRef.current!, {
          ...googleAuthButtonOptions,
          width,
        })
      }
    },
    [googleAuthButtonOptions, googleInstance]
  )

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        if (typeof window !== 'undefined') {
          const { innerWidth: width } = window

          // left padding 20px, right padding 20px
          let buttonWidth = width - 20 - 20
          buttonWidth = buttonWidth > 400 ? 400 : buttonWidth

          resizeAuthButton(buttonWidth)
        }
      })
    }
  }, [resizeAuthButton])

  // We run the effect only when the google object is loaded.
  useEffect(() => {
    try {
      googleInstance.accounts.id.initialize({
        client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID!,
        // @ts-ignore
        callback: async (response: google.accounts.id.CredentialResponse) => {
          if (isSignUp) {
            await handleSignUp(response.credential)
          } else {
            await handleSignIn(response.credential)
          }
        },
      })

      googleInstance.accounts.id.renderButton(
        buttonRef.current!,
        googleAuthButtonOptions
      )
    } catch (error) {
      setFieldError('email', 'Communication with Google failed')
    }
  }, [googleInstance])

  // constant height takes care of the layout shift when the button is rerendered when the google object is loaded.
  return (
    <div
      className="max-w-[400px] w-full h-[54px] flex justify-center"
      ref={buttonRef}
    ></div>
  )
}

export default GoogleAuthButton
